// Custom hook: useMissionPeriod
// Provides state management and business logic for mission search and updates.
import useSlideOver from 'components/SlideOver/useSlideOver';
import useModal from 'components/modal/useModal';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import UseContext from './useContext';
import Usefetchdata from './usefetchdata';

export default function useMissionPeriod() {
  // External context and dependencies
  const { objID, setObjID, searchMissionResult, setSearchMissionResult } =
    UseContext();
  const { toggleSlideOver } = useSlideOver();
  const { toggle } = useModal();
  const { userData } = Usefetchdata();
  const token = localStorage.getItem('xsrfToken');
  const [searchParams, setSearchParams] = useSearchParams();

  const initialUrlParams = useRef({
    assigned: searchParams.get('assigned'),
    sourcing: searchParams.get('sourcing'),
    departments: searchParams.get('departments'),
    status: searchParams.get('status'),
    adminid: searchParams.get('adminid'),
    mission: searchParams.get('mission'),
    search: searchParams.get('search'),
    page: searchParams.get('page'),
  });
  const pendingMissionFromUrl = useRef(initialUrlParams.current.mission || null);
  const isInitialMount = useRef(true);

  // Keep pendingMissionFromUrl alive during initial async loading (departments, employees),
  // then clear it so normal filter changes reset editMission as expected.
  useEffect(() => {
    if (pendingMissionFromUrl.current) {
      const timer = setTimeout(() => {
        pendingMissionFromUrl.current = null;
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const parseThreeState = (value) => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  };

  // Local state definitions — initialized from URL params when present
  const [searchMissionResultWaiting, setSearchMissionResultWaiting] =
    useState(false);
  const [isMissionSearchAssigned, setIsMissionSearchAssigned] = useState(
    () => initialUrlParams.current.assigned !== null
      ? parseThreeState(initialUrlParams.current.assigned)
      : null
  );
  const [isMissionSearchSourcing, setIsMissionSearchSourcing] = useState(
    () => initialUrlParams.current.sourcing !== null
      ? parseThreeState(initialUrlParams.current.sourcing)
      : null
  );
  const [isMissionSearchSigned, setIsMissionSearchSigned] = useState(null);
  const [isMissionSearchSignatureSend, setIsMissionSearchSignatureSend] =
    useState(null);
  const [isInfoMissionWaiting, setIsInfoMissionWaiting] = useState(false);
  const [isInfoMissionLoadData, setIsInfoMissionLoadData] = useState(false);
  const [editMission, setEditMission] = useState(
    () => initialUrlParams.current.mission || false
  );
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(initialUrlParams.current.page);
    return !isNaN(p) && p >= 1 ? p : 1;
  });
  const [totalPages, setTotalPages] = useState(0);
  const [limitElement] = useState(10);
  const [countPerPage, setCountPerPage] = useState(10);
  const [countTotal, setCountTotal] = useState(0);
  const [hoveredCompanyItems, setHoveredCompanyItems] = useState({});

  const [search, setSearch] = useState(
    () => initialUrlParams.current.search || ''
  );

  // Contract type filter options
  const [typeContract, setTypeContract] = useState([
    { type: 'sendToSign', name: 'Envoyé pour signature', checked: false },
    {
      type: 'toBeSentToSign',
      name: 'À envoyer pour signature',
      checked: false,
    },
    { type: 'signByCompany', name: 'Signé par l’entreprise', checked: false },
    {
      type: 'signByTempWorker',
      name: 'Signé par l’intérimaire',
      checked: false,
    },
    { type: 'notCreated', name: 'Contrat non généré', checked: false },
  ]);

  // Department filter options — loaded dynamically from API
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    ApiDatabase.getCityDepartments(
      { token },
      (data) => setDepartments(data.map((d) => ({ name: d, checked: false }))),
      () => {}
    );
  }, []);

  // Mission status filter options — initialized from URL param
  const [missionStatus, setMissionStatus] = useState(() => {
    const statusParam = initialUrlParams.current.status;
    const urlStatuses = statusParam ? statusParam.split(',') : null;
    return [
      { type: 'all', name: 'Tous les status', checked: urlStatuses ? urlStatuses.includes('all') : true },
      { type: 'done', name: 'Terminé', checked: urlStatuses ? urlStatuses.includes('done') : false },
      { type: 'inProgress', name: 'En cours', checked: urlStatuses ? urlStatuses.includes('inProgress') : false },
      { type: 'soon', name: 'À venir', checked: urlStatuses ? urlStatuses.includes('soon') : false },
    ];
  });

  // Track if we've initialized from URL params to avoid re-execution
  const hasInitializedFromParams = useRef(false);
  // Store missionId from URL to open it after search results are loaded
  const missionIdFromUrl = useRef(null);

  // Initialize filters from URL query params (after role is set to avoid conflicts)
  useEffect(() => {
    if (hasInitializedFromParams.current) return;
    if (!userData) return; // Wait for userData to be loaded

    const urlParams = new URLSearchParams(window.location.search);
    const assigned = urlParams.get('assigned');
    const status = urlParams.get('status');
    const missionId = urlParams.get('missionId');

    // Store missionId from URL if present
    if (missionId) {
      missionIdFromUrl.current = missionId;
    }

    if (assigned === 'true' || status === 'inProgress' || missionId) {
      hasInitializedFromParams.current = true;

      if (assigned === 'true') {
        setIsMissionSearchAssigned(true);
      }

      if (status === 'inProgress') {
        setMissionStatus([
          { type: 'all', name: 'Tous les status', checked: false },
          { type: 'done', name: 'Terminé', checked: false },
          { type: 'inProgress', name: 'En cours', checked: true },
          { type: 'soon', name: 'À venir', checked: false },
        ]);
      }
    }
  }, [userData]);

  const [role, setRole] = useState(500);
  const [selectedMissions, setSelectedMissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesSearch, setEmployeesSearch] = useState([]);

  // Update role state from userData
  useEffect(() => {
    if (userData?.role) {
      setRole(userData.role);
    }
  }, [userData]);

  // Fetch department missions from the API (apply URL param on first load)
  const getDepartmentMissions = () => {
    ApiDatabase.getDepartmentMissions(
      { token },
      (data) => {
        if (data.departments.length > 0) {
          const urlDepts = initialUrlParams.current.departments
            ? initialUrlParams.current.departments.split(',')
            : [];
          const deps = data.departments.map((department) => ({
            name: department,
            checked: urlDepts.includes(department),
          }));
          setDepartments(deps);
        }
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  };

  // Mémoriser les valeurs filtrées pour éviter les re-renders infinis
  const typeContractFiltered = useMemo(
    () => typeContract.filter((item) => item.checked).map((item) => item.type),
    [typeContract]
  );
  const departmentsFiltered = useMemo(
    () => departments.filter((item) => item.checked).map((item) => item.name),
    [departments]
  );
  const missionStatusFiltered = useMemo(
    () => missionStatus.filter((item) => item.checked).map((item) => item.type),
    [missionStatus]
  );
  const employeesSearchFiltered = useMemo(
    () => employeesSearch.filter((item) => item.checked).map((item) => item._id),
    [employeesSearch]
  );

  // Function to perform mission search based on filters and search input
  // Mémorisée avec useCallback pour éviter les re-renders infinis
  const manageMissionSearch = useCallback(() => {
    setSearchMissionResultWaiting(true);
    setIsInfoMissionLoadData(false);
    setIsInfoMissionWaiting(false);
    if (!pendingMissionFromUrl.current) {
      setEditMission(false);
    }

    ApiDatabase.searchMission(
      {
        token,
        search,
        isMissionSearchAssigned,
        isMissionSearchSourcing,
        typeContract: typeContractFiltered,
        departments: departmentsFiltered,
        missionStatus: missionStatusFiltered,
        employeesSearch: employeesSearchFiltered,
        limit: limitElement,
        page: currentPage,
      },
      (data) => {
        setSearchMissionResult(data.missions);
        setCountTotal(data.count);
        setCountPerPage(data.countPerPage);
        setTotalPages(data.countPages);
        
        // Open mission from URL if specified, otherwise open first mission
        if (data.missions.length > 0) {
          if (missionIdFromUrl.current) {
            // Try to find the mission with the ID from URL
            const missionFromUrl = data.missions.find(
              (mission) => mission.id_mission_real === missionIdFromUrl.current
            );
            
            if (missionFromUrl) {
              setEditMission(missionIdFromUrl.current);
              // Keep missionId in URL to reflect the current state
              missionIdFromUrl.current = null; // Clear the ref
            } else {
              // Mission not found in current page, open first mission
              setEditMission(data.missions[0].id_mission_real);
            }
          } else {
            // No missionId in URL, open first mission as default
            setEditMission(data.missions[0].id_mission_real);
          }
        }
        setSearchMissionResultWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          setSearchMissionResultWaiting(false);
          setSearchMissionResult([]);
          console.error(err);
        }
      }
    );
  }, [
    token,
    search,
    isMissionSearchAssigned,
    isMissionSearchSourcing,
    typeContractFiltered,
    departmentsFiltered,
    missionStatusFiltered,
    employeesSearchFiltered,
    limitElement,
    currentPage,
    setSearchMissionResult,
  ]);

  // Effects to trigger search when filters or pagination change
  useEffect(() => {
    manageMissionSearch();
  }, [
    isMissionSearchAssigned,
    isMissionSearchSourcing,
    typeContract,
    departments,
    missionStatus,
    employeesSearch,
    currentPage,
  ]);

  // Debounce search input change to reduce API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      manageMissionSearch();
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to first page when any filter or search input changes (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [
    isMissionSearchAssigned,
    isMissionSearchSourcing,
    typeContract,
    departments,
    missionStatus,
    employeesSearch,
    search,
  ]);

  // Sync filter state to URL params
  useEffect(() => {
    const params = new URLSearchParams();

    if (isMissionSearchAssigned !== null) {
      params.set('assigned', String(isMissionSearchAssigned));
    }
    if (isMissionSearchSourcing !== null) {
      params.set('sourcing', String(isMissionSearchSourcing));
    }

    const checkedDepts = departments.filter((d) => d.checked).map((d) => d.name);
    if (checkedDepts.length > 0) {
      params.set('departments', checkedDepts.join(','));
    }

    const checkedStatuses = missionStatus.filter((s) => s.checked).map((s) => s.type);
    if (checkedStatuses.length > 0 && !(checkedStatuses.length === 1 && checkedStatuses[0] === 'all')) {
      params.set('status', checkedStatuses.join(','));
    }

    const checkedEmployees = employeesSearch.filter((e) => e.checked).map((e) => e._id);
    if (checkedEmployees.length > 0) {
      params.set('adminid', checkedEmployees.join(','));
    }

    if (search) {
      params.set('search', search);
    }

    if (editMission) {
      params.set('mission', editMission);
    }

    if (currentPage > 1) {
      params.set('page', String(currentPage));
    }

    setSearchParams(params, { replace: true });
  }, [
    isMissionSearchAssigned,
    isMissionSearchSourcing,
    departments,
    missionStatus,
    employeesSearch,
    search,
    editMission,
    currentPage,
  ]);

  // Watch for mission param changes in URL (e.g., Cmd+K navigation while already on /missions)
  const prevMissionParam = useRef(searchParams.get('mission'));
  useEffect(() => {
    const missionParam = searchParams.get('mission');
    if (missionParam && missionParam !== prevMissionParam.current) {
      pendingMissionFromUrl.current = missionParam;
      setEditMission(missionParam);
    }
    prevMissionParam.current = missionParam;
  }, [searchParams]);

  // Initial fetch of department missions
  useEffect(() => {
    getDepartmentMissions();
  }, []);

  // Reset related search states when mission assignment filter is false
  useEffect(() => {
    if (isMissionSearchAssigned === false) {
      setIsMissionSearchSigned(false);
      setIsMissionSearchSignatureSend(false);
    }
  }, [isMissionSearchAssigned]);

  // Automatically update assignment filters based on signature status
  useEffect(() => {
    if (isMissionSearchSigned === true) {
      setIsMissionSearchAssigned(true);
      setIsMissionSearchSignatureSend(true);
    }
  }, [isMissionSearchSigned]);

  useEffect(() => {
    if (isMissionSearchSignatureSend === true) {
      setIsMissionSearchAssigned(true);
    } else if (isMissionSearchSignatureSend === false) {
      setIsMissionSearchSigned(false);
    }
  }, [isMissionSearchSignatureSend]);

  // Opens the company info slide-over panel
  const openCompanyInfo = (id) => {
    setObjID(id);
    toggleSlideOver('get_company');
  };

  // Set default search parameters based on user role (only if not initialized from URL params)
  useEffect(() => {
    if (hasInitializedFromParams.current) return; // Don't override URL params
    if (role === 525) {
      setIsMissionSearchAssigned(true);
      setIsMissionSearchSignatureSend(false);
    }
  }, [role]);

  // Determine which trash button to show based on mission state
  const shouldShowTrashButton = (mission) => {
    if (mission.status === 'cancel') return 'delete';

    const now = new Date();
    const startDate = new Date(mission.duration.datestart);
    const endDate = new Date(mission.duration.dateend);
    const hasAssignedUser = mission.user
      ? typeof mission.user === 'object'
      : false;

    let missionStatusLocal;
    if (now < startDate) {
      missionStatusLocal = 'soon';
    } else if (now > endDate) {
      missionStatusLocal = 'past';
    } else {
      missionStatusLocal = 'in_progress';
    }

    if (missionStatusLocal === 'soon' && hasAssignedUser) return 'cancel';
    if (
      (missionStatusLocal === 'in_progress' ||
        missionStatusLocal === 'soon' ||
        missionStatusLocal === 'past') &&
      !hasAssignedUser
    )
      return 'delete';

    return false;
  };

  // Open delete mission modal
  const handleDeleteMission = (idMission) => {
    setObjID(idMission);
    toggle('mission_delete');
  };

  // Open cancel mission modal
  const handleCancelMission = (idMission) => {
    setObjID(idMission);
    toggle('mission_cancel');
  };

  // Update the contract type filter state based on user selection
  const updateTypeContract = (updatedTypeContract) => {
    const updated = [...typeContract];
    for (let i = 0; i < updated.length; i++) {
      for (let j = 0; j < updatedTypeContract.length; j++) {
        if (updated[i].type === updatedTypeContract[j].value) {
          updated[i].checked = updatedTypeContract[j].checked;
        }
      }
    }
    if (updated.some((item) => item.checked)) {
      setIsMissionSearchAssigned(true);
    }
    setTypeContract(updated);
  };

  // Update the departments filter state
  const updateDepartment = (updatedDepartment) => {
    const updated = [...departments];
    for (let i = 0; i < updated.length; i++) {
      for (let j = 0; j < updatedDepartment.length; j++) {
        if (updated[i].name === updatedDepartment[j].value) {
          updated[i].checked = updatedDepartment[j].checked;
        }
      }
    }
    setDepartments(updated);
  };

  // Update the mission status filter state
  const updateMissionStatus = (updatedMissionStatus) => {
    const updated = [...missionStatus];
    for (let i = 0; i < updated.length; i++) {
      for (let j = 0; j < updatedMissionStatus.length; j++) {
        if (updated[i].type === updatedMissionStatus[j].value) {
          updated[i].checked = updatedMissionStatus[j].checked;
        }
      }
    }
    setMissionStatus(updated);
  };

  // Opens the temporary worker info slide-over panel
  const openTempWorkerInfo = (id) => {
    setObjID(id);
    toggleSlideOver('get_temp_worker');
  };

  // Handles the selection of missions for bulk actions
  const handleSelectMission = (missionId, checked) => {
    setSelectedMissions((prev) => {
      if (checked) return [...prev, missionId];
      return prev.filter((id) => id !== missionId);
    });
  };

  // Fetch employees from the API and update state (apply URL param on first load)
  useEffect(() => {
    ApiDatabase.getEmployeesToAssignMission(
      { token },
      (data) => {
        if (data.employees.length > 0) {
          const urlEmployeeIds = initialUrlParams.current.adminid
            ? initialUrlParams.current.adminid.split(',')
            : [];
          const updated = data.employees
            .map((employee) => ({
              ...employee,
              checked: urlEmployeeIds.includes(employee._id),
            }))
            .sort((a, b) => a.firstname.localeCompare(b.firstname));
          updated.push({
            firstname: 'Non assigné',
            lastname: '',
            checked: urlEmployeeIds.includes('unassign'),
            _id: 'unassign',
          });
          setEmployees(updated);
          setEmployeesSearch(updated);
        }
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, [token]);

  // Update employee search filter state, handling special case for unassigned
  const updateEmployeeSearch = (updatedEmployeeSearch) => {
    // Clone employeesSearch array to avoid direct state mutation
    const updated = employeesSearch.map((emp) => ({ ...emp }));

    // Create a mapping object for quick access to updatedEmployeeSearch items
    const searchMap = updatedEmployeeSearch.reduce((acc, item) => {
      acc[item.value] = item;
      return acc;
    }, {});

    // Check if 'unassign' is checked in updatedEmployeeSearch
    const isUnassignInUpdatedEmployeeSearchChecked = !!(
      searchMap['unassign'] && searchMap['unassign'].checked
    );
    // Find the 'unassign' item in the cloned updated array
    const unassignItem = updated.find((item) => item._id === 'unassign');
    // Check if 'unassign' is checked in updated array
    const isUnassignInUpdatedChecked = unassignItem && unassignItem.checked;

    if (
      isUnassignInUpdatedEmployeeSearchChecked &&
      !isUnassignInUpdatedChecked
    ) {
      // If 'unassign' is checked in updatedEmployeeSearch but not in updated:
      // Check 'unassign' and uncheck all others.
      updated.forEach((item) => {
        item.checked = item._id === 'unassign';
      });
    } else if (!isUnassignInUpdatedEmployeeSearchChecked) {
      // If 'unassign' is not checked in updatedEmployeeSearch:
      // Update each item based on the mapping.
      updated.forEach((item) => {
        if (searchMap[item._id] !== undefined) {
          item.checked = searchMap[item._id].checked;
        }
      });
    } else if (
      isUnassignInUpdatedEmployeeSearchChecked &&
      isUnassignInUpdatedChecked
    ) {
      // If 'unassign' is checked in both arrays:
      // Uncheck 'unassign' in updated and update the other items.
      updated.forEach((item) => {
        if (item._id === 'unassign') {
          item.checked = false;
        } else if (searchMap[item._id] !== undefined) {
          item.checked = searchMap[item._id].checked;
        }
      });
    }

    setEmployeesSearch(updated);
  };

  // Assigns an employee to multiple selected missions
  const updateEmployee = (updatedEmployee) => {
    const selectedEmployee = updatedEmployee.find(
      (employee) => employee.checked
    );
    const newEmployeeId = selectedEmployee.value;

    ApiDatabase.postMissionAdminEmployeeAssigned(
      {
        token,
        idEmployee: newEmployeeId,
        idMissions: selectedMissions,
      },
      (data) => {
        setSearchMissionResult((prevResults) =>
          prevResults.map((mission) =>
            selectedMissions.includes(mission.id_mission_real)
              ? {
                  ...mission,
                  admin_employee_assigned:
                    newEmployeeId === 'unassign' ? null : newEmployeeId,
                }
              : mission
          )
        );
        setSelectedMissions([]);
        toast.success('Assignation effectuée');
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  };

  // Assigns an employee to a single mission and updates the result list
  const updateEmployeeForMission = (missionId, updatedEmployee) => {
    const selectedEmployee = updatedEmployee.find(
      (employee) => employee.checked
    );
    const newEmployeeId = selectedEmployee.value;

    ApiDatabase.postMissionAdminEmployeeAssigned(
      {
        token,
        idEmployee: newEmployeeId,
        idMissions: [missionId],
      },
      (data) => {
        setSearchMissionResult((prevMissions) =>
          prevMissions.map((mission) =>
            mission.id_mission_real === missionId
              ? {
                  ...mission,
                  admin_employee_assigned:
                    newEmployeeId === 'unassign' ? null : newEmployeeId,
                }
              : mission
          )
        );

        toast.success('Assignation effectuée');
      },
      (err) => console.error(err)
    );
  };

  // Return all states and functions needed by the MissionPeriod component
  return {
    search,
    setSearch,
    searchMissionResult,
    searchMissionResultWaiting,
    isMissionSearchAssigned,
    setIsMissionSearchAssigned,
    isMissionSearchSourcing,
    setIsMissionSearchSourcing,
    typeContract,
    updateTypeContract,
    departments,
    updateDepartment,
    missionStatus,
    updateMissionStatus,
    employees,
    employeesSearch,
    updateEmployee,
    updateEmployeeSearch,
    openCompanyInfo,
    openTempWorkerInfo,
    handleDeleteMission,
    handleCancelMission,
    handleSelectMission,
    selectedMissions,
    shouldShowTrashButton,
    updateEmployeeForMission,
    currentPage,
    setCurrentPage,
    totalPages,
    countPerPage,
    countTotal,
    editMission,
    setEditMission,
    hoveredCompanyItems,
    setHoveredCompanyItems,
    setSearchMissionResult,
  };
}
